import { useContext, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { useFlags, useFlagsLoaded } from "@/hooks/useFeatureFlags";
import { messageTemplatesEnabled } from "@/hooks/useFeatureFlags/flags";
import { AuthorizationContext, CareerConsultantSectionsContext } from "./AuthorizationContext";
import { CAREER_CONSULTANT_SECTIONS_QUERY } from "./queries";
import { CareerConsultantSection, ROLE } from "./types";

type CoverageState = {
  careerConsultantSections: CareerConsultantSection[];
  settled: boolean;
};

const INITIAL_COVERAGE_STATE: CoverageState = {
  careerConsultantSections: [],
  settled: false
};

export default function CareerConsultantSectionsProvider({ children }: { children: React.ReactNode }) {
  const { loaded, roles } = useContext(AuthorizationContext);
  const flags = useFlags();
  const flagsLoaded = useFlagsLoaded();
  const isGlobalAdmin = roles.includes(ROLE.GLOBAL_ADMIN);
  const templatesEnabled = flagsLoaded && messageTemplatesEnabled(flags);
  const skip = !loaded || !templatesEnabled || isGlobalAdmin;
  const [state, setCoverage] = useState<CoverageState>(INITIAL_COVERAGE_STATE);

  useQuery(CAREER_CONSULTANT_SECTIONS_QUERY, {
    fetchPolicy: "cache-first",
    errorPolicy: "all",
    skip,
    onCompleted({ careerConsultantSections = [] }) {
      setCoverage((currentState) => {
        const nextSections = careerConsultantSections;
        const sameCoverage =
          currentState.settled &&
          currentState.careerConsultantSections.length === nextSections.length &&
          currentState.careerConsultantSections.every((section, index) => section.id === nextSections[index]?.id);

        if (sameCoverage) {
          return currentState;
        }

        return {
          careerConsultantSections: nextSections,
          settled: true
        };
      });
    },
    onError() {
      setCoverage((currentState) => (currentState.settled ? currentState : { ...currentState, settled: true }));
    }
  });

  const value = useMemo(
    () => ({
      careerConsultantSections: skip ? [] : state.careerConsultantSections,
      careerConsultantSectionsLoading: !skip && !state.settled
    }),
    [skip, state.careerConsultantSections, state.settled]
  );

  return <CareerConsultantSectionsContext.Provider value={value}>{children}</CareerConsultantSectionsContext.Provider>;
}
